import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Typography from "@mui/material/Typography";

import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  Card,
  Grid,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";
import { keyframes, width } from "@mui/system";
import { Country, State, City } from "country-state-city";
import Pagination from "@mui/material/Pagination";

function Location() {
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locationList, setLocationList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [locationDetails, setLocationDetails] = useState({
    address: "",
    city: "",
    state: "",
    pin: "",
    country: "",
  });

  const navigate = useNavigate();

  // Fetch countries on mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

 useEffect(() => {
  if (locationDetails.country) {
    const countryStates = State.getStatesOfCountry(locationDetails.country);
    setStates(countryStates || []);
  } else {
    setStates([]);
  }
}, [locationDetails.country]);

// Fetch cities whenever state changes (except when editing)
useEffect(() => {
  if (locationDetails.country && locationDetails.state) {
    const stateCities = City.getCitiesOfState(locationDetails.country, locationDetails.state);
    setCities(stateCities || []);
  } else {
    setCities([]);
  }
}, [locationDetails.country, locationDetails.state]);

// Input change handler
const handleInputChange = (e) => {
  const { name, value } = e.target;
  setLocationDetails((prev) => ({ ...prev, [name]: value }));
};

// --- Handle edit modal ---
const handleOpenEditModal = (id) => {
  
  const location = locationList.find((loc) => loc.id === id);
  
  if (location) {
    // 1️⃣ Populate states and cities first
    const countryStates = State.getStatesOfCountry(location.country) || [];
    setStates(countryStates);

    const stateCities = City.getCitiesOfState(location.country, location.state) || [];
    setCities(stateCities);

    // 2️⃣ Set location details AFTER states/cities are ready
    setLocationDetails({
      address: location.address || "",
      country: location.country || "",
      state: location.state || "",
      city: location.city || "",
      pin: location.pin || "",
    });

    setSelectedId(id);
    setOpenEditModal(true);
  }
};

  const handleOpenModal = () => {
  setLocationDetails({
    address: "",
    city: "",
    state: "",
    pin: "",
    country: "",
  });

  setStates([]);
  setCities([]);

  setOpenModal(true);
};

  const handleCloseModal = () => setOpenModal(false);

 
  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setLocationDetails({ address: "", city: "", state: "", pin: "", country: "" });
  };

  const handleOpenDeleteModal = (id) => {
    setSelectedId(id);
    setDeleteModal(true);
  };
  const handleCloseDeleteModal = () => setDeleteModal(false);

  // Fetch locations
  const fetchLocation = async ( pageNumber = 1 ) => {
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      if (!token) throw new Error("Authentication token not found.");

      const response = await axios.get("/api/location/getAllLocations",
         { 
          params: { page: Math.max(0, pageNumber - 1) }, 
          headers: 
          { Authorization: `Bearer ${token}` }
         });
      if (response.status === 200) {
        const decryptedData = decryptBody(response.data, secretKey);
        setLocationList(decryptedData.content || []);
        setTotalPages(decryptedData.totalPages);
        setCurrentPage(decryptedData.number + 1);
      }
    } catch (error) {
      toast.error("Failed to fetch locations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocation();
  }, []);

   const handlePageChange = (_, pageNumber) => {
    setCurrentPage(pageNumber);
    fetchLocation(pageNumber); 
  };

  // Create Location
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const secretKey = await fetchSecretKey();
      const encryptedData = encryptBody(locationDetails, secretKey);
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;

      const response = await axios.post("/api/location/create", encryptedData, {
        headers: { "Content-Type": "text/plain", Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);
        toast.success(decryptedResponse.message || "Location added successfully!");
        setLocationList([...locationList, locationDetails]);
        //setLocationDetails({ address: "", city: "", state: "", pin: "", country: "",id:"" });
        fetchLocation(); 
        handleCloseModal();
      }
    } catch (error) {
      toast.error("Failed to add location.");
    }
  };

  // Update Location
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      const encryptedData = encryptBody({ id: selectedId, ...locationDetails }, secretKey);

      const response = await axios.post("/api/location/update", encryptedData, {
        headers: { "Content-Type": "text/plain", Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);
        toast.success(decryptedResponse.message || "Location updated successfully!");
        setOpenEditModal(false);
        fetchLocation(currentPage);
      }
    } catch (error) {
      toast.error(error.message || "Failed to update location.");
    }
  };

  // Delete Location
  const handleDelete = async () => {
    try {
      const secretKey = await fetchSecretKey();
      const token = JSON.parse(localStorage.getItem("loginData"))?.token;
      const encryptedData = encryptBody({ id: selectedId }, secretKey);

      const response = await axios.post("/api/location/delete", encryptedData, {
        headers: { "Content-Type": "text/plain", Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        const decryptedResponse = decryptBody(response.data, secretKey);
        toast.success(decryptedResponse.message || "Location deleted successfully!");
        setDeleteModal(false);
        fetchLocation();
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete location.");
    }
  };

  // Loader Animation
  const spinAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;
  const dotPositions = [
    { top: "5%", left: "50%" },
    { top: "20%", left: "80%" },
    { top: "50%", left: "95%" },
    { top: "80%", left: "80%" },
    { top: "95%", left: "50%" },
    { top: "80%", left: "20%" },
    { top: "50%", left: "5%" },
    { top: "20%", left: "20%" },
  ];

  // Render dropdown component with fixed height
  const renderLocationDropdowns = () => (
    <>
      <Grid item xs={12}>
        <MDInput
          type="text"
          label="Address"
          name="address"
          fullWidth
          value={locationDetails.address}
          onChange={handleInputChange}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: "45px" } }}>
          <InputLabel>Country</InputLabel>
          <Select
            name="country"
            value={locationDetails.country}
            onChange={handleInputChange}
            label="Country"
          >
            {countries.map((c) => (
              <MenuItem key={c.isoCode} value={c.isoCode}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: "45px" } }}>
          <InputLabel>State</InputLabel>
          <Select
            name="state"
            value={locationDetails.state}
            onChange={handleInputChange}
            label="State"
          >
            {states.map((s) => (
              <MenuItem key={s.isoCode} value={s.isoCode}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth sx={{ "& .MuiInputBase-root": { height: "45px" } }}>
          <InputLabel>City</InputLabel>
          <Select
            name="city"
            value={locationDetails.city}
            onChange={handleInputChange}
            label="City"
          >
            {cities.map((c) => (
              <MenuItem key={c.name} value={c.name}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <MDInput
          type="text"
          label="Pincode"
          name="pin"
          fullWidth
          value={locationDetails.pin}
          onChange={handleInputChange}
        />
      </Grid>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 80,
              height: 80,
              animation: `${spinAnimation} 1.5s linear infinite`,
            }}
          >
            {dotPositions.map((pos, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor:"#2e7faeff",
                  position: "absolute",
                  ...pos,
                }}
              />
            ))}
          </Box>
        </Box>
      )}

     <MDBox
  mt={1}
  p={1}
  display="flex"
  justifyContent="space-between"
  alignItems="center"
>
  <MDTypography variant="h5">Location Management</MDTypography>

  <MDButton
    variant="gradient"
    
    onClick={handleOpenModal}
    sx={{
      background: "#2e7faeff !important",  // force override gradient bg
      color: "#fff !important",
      boxShadow: "none !important",
      "&:hover": {
        background: "#2e7faeff !important", // keep same color
        boxShadow: "none !important",
      },
      "&:focus": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
        outline: "none",
      },
      "&:active": {
        background: "#2e7faeff !important",
        boxShadow: "none !important",
      },
    }}
  >
    Add Location
  </MDButton>
</MDBox>

      <MDBox sx={{ overflowX: "auto", width: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{ mt: 3, border: "1px solid #ddd", borderRadius: "8px", boxShadow: "2px 4px 10px rgba(0,0,0,0.1)" }}
        >
          <Table sx={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2e7faeff" }}>
                {["Address", "City", "State", "Pincode", "Country", "Actions"].map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid #ddd",
                      textAlign: "center",
                      padding: "7px",
                      minWidth: index === 0 ? "250px" : "158px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            
      <TableBody>
        {locationList.length > 0 ? (
          locationList.map((dealer, index) => (
           <TableRow
  key={index}
  
 sx={{
    cursor: "pointer",
    "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
    "&:hover": { backgroundColor: "#f0f8ff" },            
  }}
>
           
               <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "150px" }}>
                {dealer.address}
              </TableCell>
             
  <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "150px" }}>
  {/* <span style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}> */}
    {dealer.city}
  {/* </span> */}
</TableCell>

                <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.state}
               
              </TableCell>
               <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.pin}
               
              </TableCell>
               <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.country}
               
              </TableCell>
             
              {/* <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.gender}
              </TableCell>
               <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.martialStatus}
              </TableCell>
                <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.certificationvalidtill}
              </TableCell>
                <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.dob}
              </TableCell>
                <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.age}
              </TableCell> */}
                {/* <TableCell sx={{ border: "1px solid #ddd", textAlign: "center", padding: "12px", width: "120px" }}>
                {dealer.locationid}
              </TableCell> */}

             

              
                

              <TableCell sx={{ textAlign: "center", border: "1px solid #ddd" }}>
                      <IconButton color="default" onClick={() => handleOpenEditModal(dealer.id)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="default" onClick={() => handleOpenDeleteModal(dealer.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={8} align="center" sx={{ border: "1px solid #ddd", padding: "12px", fontStyle: "italic", color: "gray" }}>
              No Data Available.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
            </TableHead>
            <Box mt={2} display="flex" justifyContent="center">
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="success"
                         sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: "4px",
                  color: "#2e7faeff",          // Text color
                  borderColor: "#2e7faeff",    // Border color if outlined
                },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#2e7faeff", // Selected background
                  color: "#fff",                 // Selected text color
                },
                "& .MuiPaginationItem-root:hover": {
                  backgroundColor: "#2e7faeff33", // Slight hover effect
                },
              }}
                      />
                    </Box>
          </Table>
        </TableContainer>
      </MDBox>

      {/* Add Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Card sx={{ width: "700px", padding: 4, maxHeight: "90vh", overflowY: "auto", margin: "auto", mt: 5 }}>
          <form onSubmit={handleSubmit}>
            <MDTypography sx={{ textAlign: "center", fontSize: "15px", fontWeight: "bold", mb: 2 }}>
              Add Location Details
            </MDTypography>
            <Grid container spacing={2}>
              {renderLocationDropdowns()}
            </Grid>
            <MDBox mt={3} display="flex" justifyContent="space-between">
              <MDButton type="submit" variant="gradient" sx={{
      backgroundColor: "#2e7faeff",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#2e7faeff", 
      },
      "&:focus": {
        backgroundColor: "#2e7faeff", 
      },
      "&:active": {
        backgroundColor: "#2e7faeff", 
      },
    }}>
                Save
              </MDButton>
              <MDButton variant="gradient" onClick={handleCloseModal} sx={{
      backgroundColor: "#2e7faeff",
      color: "#fff",
      "&:hover": {
        backgroundColor: "#2e7faeff", 
      },
      "&:focus": {
        backgroundColor: "#2e7faeff", 
      },
      "&:active": {
        backgroundColor: "#2e7faeff", 
      },
    }}>
                Cancel
              </MDButton>
            </MDBox>
          </form>
        </Card>
      </Modal>

      {/* Edit Modal */}
      <Modal open={openEditModal} onClose={handleCloseEditModal}>
        <Card sx={{ width: "700px", padding: 4, maxHeight: "90vh", overflowY: "auto", margin: "auto", mt: 5 }}>
          <form onSubmit={handleSubmitEdit}>
            <MDTypography sx={{ mb: 2, textAlign: "center", fontSize: "15px", fontWeight: "bold" }}>
              Edit Location
            </MDTypography>
            <Grid container spacing={2}>
              {renderLocationDropdowns()}
            </Grid>
            <MDBox gap={2} mt={3} display="flex" justifyContent="center">
              <MDButton type="submit" variant="gradient" sx={{
    background: "#1976D2",
    color: "#fff",
    "&:hover": {
      background: "#1976D2", 
    },
  }}>
                Update
              </MDButton>
              <MDButton variant="gradient" onClick={handleCloseEditModal} sx={{
    background: "#9E9E9E",
    color: "#fff",
    "&:hover": {
      background: "#9E9E9E", // darker shade on hover
    },
  }}>
                Cancel
              </MDButton>
            </MDBox>
          </form>
        </Card>
      </Modal>

      {/* Delete Modal */}
   <Modal open={deleteModal} onClose={handleCloseDeleteModal}>
        <Card sx={{ width: "400px", padding: 4, margin: "auto", mt: 15, textAlign: "center" }}>
          <MDTypography sx={{ mb: 3, fontSize: "16px", fontWeight: "bold" }}>
            Are you sure you want to delete this Location Details?
          </MDTypography>
   <MDBox display="flex" justifyContent="space-around">
            <MDButton variant="gradient" onClick={handleDelete} sx={{ background: "red", color: "#fff" }}>
              Delete
            </MDButton>
            <MDButton
              variant="gradient"
              onClick={handleCloseDeleteModal}
              sx={{ background: "#2e7faeff", color: "#fff" }}
            >
              Cancel
            </MDButton>
          </MDBox>

        </Card>
      </Modal>
    </DashboardLayout>
  );
}

export default Location;
